<template>
  <main class="voice-page sst-wide grow">
    <section class="voice-hero">
      <div class="voice-shell voice-hero-grid">
        <div class="voice-copy">
          <div class="voice-product-mark">
            <span aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M6.6 4.5c.5-.5 1.2-.6 1.8-.3l2.3 1.3c.6.3.9 1.1.7 1.7l-.7 2.2c-.1.4 0 .8.3 1.1l2.4 2.4c.3.3.7.4 1.1.3l2.2-.7c.7-.2 1.4.1 1.8.7l1.3 2.3c.3.6.2 1.3-.3 1.8l-1.1 1.1c-.8.8-2 1.1-3.1.8-5.4-1.4-9.6-5.6-11-11-.3-1.1 0-2.3.8-3.1l1.5-.6Z" />
              </svg>
            </span>
            Workspace Voice
          </div>

          <h1>{{ story.heroTitle }}</h1>
          <p>{{ story.heroCopy }}</p>
          <p class="voice-plan-note">{{ story.heroPlanNote }}</p>

          <div class="voice-actions">
            <a
              :href="addReferralToUrl(storyTrialUrl(story))"
              class="voice-primary"
              data-conversion-event="start_free_click"
              data-conversion-label="Workspace Voice hero start free"
              data-conversion-location="workspace-voice-hero"
            >
              Start free trial
            </a>
            <!-- Terms directly under the trial button (B1.1), also when the row wraps on phones. -->
            <TrialDisclosure :plan="storyTrialPlan(story)" />
            <NuxtLink
              :to="localePath('/contact-sales')"
              class="voice-secondary"
              data-conversion-event="demo_request_click"
              data-conversion-label="Workspace Voice hero demo"
              data-conversion-location="workspace-voice-hero"
            >
              Request a demo
            </NuxtLink>
          </div>
        </div>

        <!-- A real-app hero (story.hero.replica) plays through StoryReplicaStage, as on the other story pages. -->
        <div class="voice-hero-art voice-hero-story" :class="{ 'voice-hero-story--app': story.hero?.app }">
          <StoryReplicaStage
            v-if="story.hero?.replica"
            class="feature-hero-frame"
            :replica="story.hero.replica"
            :app="story.hero.app"
            :states="story.hero.states"
            :holds="story.hero.holds"
            :label="story.hero.label"
            autoplay
          />
          <StoryHero v-else :hero="story.hero" :label="story.heroLabel" />
        </div>
      </div>
    </section>


    <section id="what-is" class="voice-definition-section">
      <div class="voice-shell voice-definition-grid">
        <div>
          <span class="voice-definition-label">Workspace Voice definition</span>
          <h2>What is a business phone system with CRM?</h2>
        </div>
        <div>
          <p class="voice-definition-answer">
            A business phone system with CRM connects calling, routing, voicemail, SMS,
            recordings, and follow-up to the customer record. Workspace Voice adds those
            communication tools inside the Workspace369 Inbox suite so conversations stay
            linked to client work, billing, files, and tasks.
          </p>
          <div class="voice-reference-row">
            <p>
              Reviewed October 21, 2026 by the Workspace369 Product Team. Voice availability can
              vary by plan, number eligibility, region, and carrier requirements.
            </p>
            <div aria-label="Workspace Voice documentation">
              <a
                href="https://docs.workspace369.com/v/2026.3/communication/workspace-voice/"
                target="_blank"
                rel="noopener noreferrer"
              >Voice overview</a>
              <a
                href="https://docs.workspace369.com/v/2026.3/communication/voice-phone-number-and-routing/"
                target="_blank"
                rel="noopener noreferrer"
              >Routing guide</a>
              <a
                href="https://docs.workspace369.com/v/2026.3/communication/voice-voicemail-recordings-and-ai/"
                target="_blank"
                rel="noopener noreferrer"
              >Recordings and AI</a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <FeatureTutorial v-if="featureTutorial" :tutorial="featureTutorial" />

    <div id="how-it-works">
      <StoryFeatureStory :story="story" />
    </div>

    <section id="faq" class="voice-faq-section">
      <div class="voice-shell faq-shell">
        <div class="section-heading">
          <h2>Workspace Voice questions.</h2>
        </div>

        <div class="voice-faq-grid">
          <details v-for="(item, index) in voiceFaqItems" :key="item.question" :open="index === 0">
            <summary>{{ item.question }}</summary>
            <p>{{ item.answer }}</p>
          </details>
        </div>
      </div>
    </section>

    <section class="voice-related-section">
      <div class="voice-shell voice-related-layout">
        <div class="section-heading">
          <h2>Next in the phone system.</h2>
        </div>
        <nav class="voice-related-list" aria-label="Related Workspace369 pages">
          <NuxtLink
            v-for="link in voiceRelatedPages"
            :key="link.path"
            :to="localePath(link.path)"
          >
            <span>{{ link.label }}</span>
            <span aria-hidden="true">-&gt;</span>
          </NuxtLink>
        </nav>
      </div>
    </section>

    <FeatureReleaseUpdates path="/features/workspace-voice" />
    <Cta03
      :title="story.ctaTitle"
      body="Start a trial or book a walkthrough to see how Workspace Voice fits your client communication workflow."
      plain
      :signup-url="storyTrialUrl(story)"
    />
    <PhoneStickyCta
      :href="storyTrialUrl(story)"
      :plan="storyTrialPlan(story)"
      label="Workspace Voice"
      location="/features/workspace-voice"
      watch=".voice-hero .voice-actions"
    />
  </main>
</template>

<script setup lang="ts">
import featureVideos from "#build/page-data/feature-videos.mjs";
import { localizedUrl } from '~/data/locales';
import storyLoaders from '#build/page-data/stories-loaders.mjs';
import { storyTrialPlan, storyTrialUrl } from '~/data/story-trial-url';

const featureTutorial = featureVideos["/features/workspace-voice"];

const { addReferralToUrl } = useReferral();
const { canonicalLocaleCode, localePath } = useWorkspaceLocale();
const siteUrl = 'https://workspace369.com';
const pagePath = '/features/workspace-voice';
const canonicalUrl = computed(() => localizedUrl(siteUrl, pagePath, canonicalLocaleCode.value));

// Story mode (data/feature-stories.ts): the call card hero and the chapters.
const story = await storyLoaders['workspace-voice']();

const voiceFaqItems = [
  {
    question: 'What is Workspace Voice?',
    answer:
      'Workspace Voice is the business calling layer inside the Workspace369 Inbox suite, connecting inbound and outbound calling, routing, voicemail, SMS, MMS, AI notes, call summaries, and client records.',
  },
  {
    question: 'What AI features work with Workspace Voice?',
    answer:
      'Workspace Voice works with Workspace AI for AI call notes, conversation summaries, follow-up drafting, voicemail review, task and reminder creation, rewrite and translation support, and searchable client context. AI call summaries and voicemail transcription start on Specialist.',
  },
  {
    question: 'Does Workspace Voice include call routing and SMS?',
    answer:
      'Yes. SMS, MMS, group texts and no-answer forwarding start on Voyager; ring groups, IVR menus, call queues, business-hours routing, presence and DND start on Specialist. Texts sit in the same Workspace369 Inbox, with scheduled send, quick responses, AI writing tools, and compliance controls.',
  },
  {
    question: 'Can Workspace Voice replace a separate business phone app?',
    answer:
      'For teams that want calls, texts, voicemail, client context, invoices, files, tasks, and AI follow-up in one workspace, Workspace Voice is designed to reduce the need for a separate phone tab and disconnected communication history.',
  },
  {
    question: 'Does Workspace Voice save call history to the CRM?',
    answer:
      'Yes. Calls can stay connected to client records with duration, status, recording access, notes, summaries, reminders, tasks, and follow-up actions where configured.',
  },
  {
    question: 'Which Workspace369 plan includes Workspace Voice?',
    answer:
      'Business calling, voicemail, and call logs start on the Voyager plan. The full phone system, with IVR menus, ring groups, call queues, and call recording, is on Specialist and higher, with unlimited workflows and dedicated support on Commander.',
  },
  {
    question: 'Why does a dedicated business number need carrier compliance setup?',
    answer:
      'Business SMS carriers require registration before dedicated numbers can send and receive business texts reliably. Workspace369 lists the $50 one-time carrier compliance setup separately so teams see the regulated cost before claiming one or more numbers.',
  },
];

const voiceRelatedPages = [
  { label: 'Post-call automation', path: '/features/post-call-automation' },
  { label: 'Auto-attendant and IVR', path: '/features/auto-attendant-and-ivr' },
  { label: 'Client communications', path: '/features/communications' },
  { label: 'Business phone system with CRM', path: '/solutions/business-phone-system-with-crm' },
  { label: 'Google Voice alternative', path: '/solutions/google-voice-alternative' },
  { label: 'Workspace369 vs RingCentral', path: '/compare/ringcentral' },
];

useHead(() => ({
  title: 'Workspace Voice | AI Phone and SMS',
  meta: [
    {
      name: 'description',
      content:
        'Workspace Voice adds business calls, transfers, ring groups, voicemail, call recording, AI call summaries, missed-call text back and SMS to your client inbox.',
    },
    {
      name: 'twitter:title',
      content: 'Workspace Voice | AI Phone and SMS',
    },
    {
      name: 'twitter:description',
      content:
        'Business calling, transfers, ring groups, voicemail, call recording, AI call summaries, missed-call text back and SMS follow-up inside Workspace369.',
    },
    { name: 'twitter:card', content: 'summary_large_image' },
    {
      property: 'og:title',
      content: 'Workspace Voice | AI Phone and SMS',
    },
    {
      property: 'og:description',
      content:
        'Business calling, transfers, ring groups, voicemail, call recording, AI call summaries, missed-call text back and SMS follow-up inside Workspace369.',
    },
    { property: 'og:url', content: canonicalUrl.value },
  ],
  link: [{ key: 'canonical', rel: 'canonical', href: canonicalUrl.value }],
  script: [
    {
      key: 'workspace-voice-schema',
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Service',
            '@id': `${canonicalUrl.value}#service`,
            name: 'Workspace Voice',
            serviceType: 'Business phone system and inbox calling',
            provider: { '@id': `${siteUrl}/#organization` },
            url: canonicalUrl.value,
            description:
              'Business calling, routing, IVR, queues, voicemail, call recording, SMS, MMS, AI call summaries, transcription, and client context inside the Workspace369 Inbox suite.',
            areaServed: 'US',
          },
          {
            '@type': 'FAQPage',
            '@id': `${canonicalUrl.value}#faq`,
            mainEntity: voiceFaqItems.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer,
              },
            })),
          },
          {
            '@type': 'BreadcrumbList',
            '@id': `${canonicalUrl.value}#breadcrumbs`,
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: `${siteUrl}/`,
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Features',
                item: localizedUrl(siteUrl, '/features', canonicalLocaleCode.value),
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: 'Workspace Voice',
                item: canonicalUrl.value,
              },
            ],
          },
        ],
      }),
    },
  ],
}));
</script>

<style scoped>
.voice-page {
  --voice-action-radius: 0.375rem;

  background: #ffffff;
  color: #0f172a;
  letter-spacing: 0;
}

.voice-shell {
  width: min(100% - 2rem, 68rem);
  margin: 0 auto;
}

.voice-hero {
  overflow: hidden;
  background:
    radial-gradient(circle at 78% 12%, rgb(27 167 206 / 0.12), transparent 21rem),
    linear-gradient(180deg, #ffffff, #f8fafc);
  padding: 9.5rem 0 4.5rem;
}

.voice-hero-grid {
  display: grid;
  gap: 3rem;
  align-items: center;
}

.voice-product-mark {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  color: #0f172a;
  font-size: 0.86rem;
  font-weight: 750;
}

.voice-product-mark span {
  display: inline-flex;
  height: 1.7rem;
  width: 1.7rem;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #e0f7fd;
  color: #0e7490;
}

.voice-product-mark svg {
  height: 1rem;
  width: 1rem;
  fill: currentColor;
}

.voice-copy h1 {
  margin-top: 1rem;
  max-width: 37rem;
  color: #0f172a;
  font-size: clamp(2rem, 3.35vw, 2.82rem);
  font-weight: 850;
  line-height: 1.08;
}

.voice-copy p {
  margin-top: 1.25rem;
  max-width: 38rem;
  color: #475569;
  font-size: clamp(1rem, 1.5vw, 1.12rem);
  line-height: 1.75;
}

.voice-copy .voice-plan-note {
  margin-top: 0.75rem;
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.5;
}
.voice-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  margin-top: 1.8rem;
}

.voice-primary,
.voice-secondary {
  display: inline-flex;
  min-height: 2.9rem;
  align-items: center;
  justify-content: center;
  border-radius: var(--voice-action-radius);
  padding: 0 1.2rem;
  font-size: 0.86rem;
  font-weight: 800;
  transition:
    transform 160ms ease,
    box-shadow 160ms ease,
    border-color 160ms ease;
}

.voice-primary {
  background: #0e7490;
  color: #ffffff;
  box-shadow: 0 14px 32px rgb(27 167 206 / 0.24);
}

.voice-secondary {
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #0f172a;
}

.voice-primary:hover,
.voice-secondary:hover {
  transform: translateY(-1px);
}

.voice-secondary:hover {
  border-color: rgb(27 167 206 / 0.45);
  box-shadow: 0 14px 32px rgb(15 23 42 / 0.08);
}

.voice-hero-art {
  position: relative;
  min-height: 25rem;
}

.voice-hero-story {
  min-height: 0;
}

#how-it-works {
  scroll-margin-top: 6rem;
}
.phone-number {
  display: block;
  text-align: center;
}

.phone-number {
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 700;
}

.voice-definition-section {
  scroll-margin-top: 8rem;
  border-block: 1px solid #dbe3ee;
  background: #f8fafc;
  padding: 3.8rem 0;
}

.voice-definition-grid {
  display: grid;
  gap: 1.5rem;
}

.voice-definition-label {
  color: #0369a1;
  font-size: 0.76rem;
  font-weight: 900;
  text-transform: uppercase;
}

.voice-definition-grid h2 {
  max-width: 29rem;
  margin-top: 0.7rem;
  color: #0f172a;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 900;
  line-height: 1.06;
}

.voice-definition-answer {
  max-width: 43rem;
  color: #334155;
  font-size: clamp(1.05rem, 1.8vw, 1.25rem);
  line-height: 1.7;
}

.voice-reference-row {
  display: grid;
  gap: 0.85rem;
  margin-top: 1.25rem;
  border-top: 1px solid #cbd5e1;
  padding-top: 1rem;
}

.voice-reference-row p {
  max-width: 43rem;
  color: #64748b;
  font-size: 0.8rem;
  line-height: 1.55;
}

.voice-reference-row div {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem 1rem;
}

.voice-reference-row a {
  color: #0e7490;
  font-size: 0.8rem;
  font-weight: 850;
}

.voice-faq-section {
  scroll-margin-top: 8rem;
}
.section-heading h2 {
  color: #0f172a;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 850;
  line-height: 1.08;
}
.section-heading p {
  margin-top: 1rem;
  max-width: 35rem;
  color: #475569;
  font-size: 1rem;
  line-height: 1.72;
}

.section-heading {
  text-align: center;
}

.section-heading p {
  margin-right: auto;
  margin-left: auto;
}

.voice-faq-section {
  padding: 4.5rem 0;
  background:
    radial-gradient(circle at 12% 8%, rgb(27 167 206 / 0.11), transparent 22rem),
    linear-gradient(180deg, #f8fafc, #ffffff);
}

.faq-shell {
  display: grid;
  gap: 2.1rem;
}

.voice-faq-grid {
  display: grid;
  gap: 0.85rem;
}

.voice-faq-grid details {
  border: 1px solid #dbe3ee;
  border-radius: 0.95rem;
  background: rgb(255 255 255 / 0.94);
  box-shadow: 0 16px 44px rgb(15 23 42 / 0.055);
}

.voice-faq-grid summary {
  cursor: pointer;
  list-style: none;
  padding: 1.05rem 1.15rem;
  color: #0f172a;
  font-size: 0.98rem;
  font-weight: 850;
  line-height: 1.35;
}

.voice-faq-grid summary::-webkit-details-marker {
  display: none;
}

.voice-faq-grid summary::after {
  float: right;
  margin-left: 1rem;
  color: #1ba7ce;
  content: "+";
  font-size: 1.15rem;
  line-height: 1;
}

.voice-faq-grid details[open] summary::after {
  content: "-";
}

.voice-faq-grid details p {
  border-top: 1px solid #e2e8f0;
  padding: 0.95rem 1.15rem 1.15rem;
  color: #64748b;
  font-size: 0.92rem;
  line-height: 1.62;
}

.voice-related-section {
  border-top: 1px solid #dbe3ee;
  border-bottom: 1px solid #dbe3ee;
  background: #ffffff;
  padding: 4rem 0;
}

.voice-related-layout {
  display: grid;
  gap: 2rem;
}

.voice-related-layout .section-heading {
  text-align: left;
}

.voice-related-list {
  display: grid;
  border-top: 1px solid #dbe3ee;
}

.voice-related-list a {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border-bottom: 1px solid #dbe3ee;
  padding: 1rem 0;
  color: #0f172a;
  font-size: 0.93rem;
  font-weight: 850;
}

.voice-related-list a > span:last-child {
  color: #0e7490;
}

.voice-related-list a:hover {
  color: #0e7490;
}


@media (min-width: 960px) {
  .voice-definition-grid {
    grid-template-columns: 0.78fr 1.22fr;
    align-items: start;
  }
  .voice-hero-grid {
    grid-template-columns: minmax(0, 0.92fr) minmax(0, 1.08fr);
  }

  .voice-related-layout {
    grid-template-columns: 0.78fr 1.22fr;
    align-items: start;
  }
}


@media (max-width: 719px) {
  .voice-shell {
    width: min(100% - 1rem, 68rem);
  }

  .voice-hero {
    padding: 6.25rem 0 2.1rem;
  }

  .voice-hero-grid {
    gap: 1.25rem;
  }

  .voice-copy h1 {
    margin-top: 0.75rem;
    max-width: 26rem;
    font-size: clamp(1.8rem, 7.4vw, 2.25rem);
    line-height: 1.08;
  }

  .voice-copy p {
    margin-top: 0.9rem;
    font-size: 0.95rem;
    line-height: 1.56;
  }
  .voice-actions {
    gap: 0.65rem;
    margin-top: 1.15rem;
  }

  .voice-hero-art {
    min-height: 0;
  }

  .voice-definition-section {
    padding: 2.75rem 0;
  }
  .voice-faq-section {
    padding: 2.85rem 0;
  }
  .section-heading h2 {
    font-size: clamp(1.65rem, 7.5vw, 2.25rem);
    line-height: 1.08;
  }
  .section-heading p {
    margin-top: 0.75rem;
    font-size: 0.92rem;
    line-height: 1.58;
  }

  .voice-faq-grid {
    gap: 0.75rem;
    margin-top: 1.35rem;
  }

  .voice-faq-grid details {
    border-radius: 0.85rem;
  }

  .voice-faq-grid summary {
    padding: 0.85rem 0.9rem;
    font-size: 0.86rem;
  }

  .voice-faq-grid details p {
    padding: 0.75rem 0.9rem 0.9rem;
    font-size: 0.8rem;
    line-height: 1.48;
  }

}

@media (max-width: 460px) {
  .voice-actions {
    display: grid;
  }

  .voice-primary,
  .voice-secondary {
    width: 100%;
  }

  .voice-product-mark {
    font-size: 0.78rem;
  }

}

.dark .voice-page {
  background: #020617;
  color: #e2e8f0;
}

.dark .voice-hero {
  background:
    radial-gradient(circle at 78% 12%, rgb(27 167 206 / 0.2), transparent 21rem),
    linear-gradient(180deg, #020617, #0f172a);
}

.dark .voice-definition-section {
  border-color: rgb(148 163 184 / 0.2);
  background: #020617;
}

.dark .voice-definition-grid h2 {
  color: #ffffff;
}

.dark .voice-definition-answer {
  color: #cbd5e1;
}

.dark .voice-reference-row {
  border-color: rgb(148 163 184 / 0.2);
}

.dark .voice-reference-row p {
  color: #94a3b8;
}

.dark .voice-reference-row a {
  color: #67e8f9;
}
.dark .voice-product-mark,
.dark .voice-copy h1,
.dark .section-heading h2,
.dark .voice-faq-grid summary {
  color: #ffffff;
}
.dark .voice-copy p,
.dark .section-heading p,
.dark .phone-number,
/* Blue labels: #0369a1 on the light sections,
sky blue on dark. */
.dark .voice-definition-label {
  color: #38bdf8;
}

.dark .voice-faq-grid details p {
  color: #cbd5e1;
}

.dark .voice-secondary,
.dark .voice-faq-grid details {
  border-color: rgb(148 163 184 / 0.22);
  background: rgb(15 23 42 / 0.88);
}

.dark .voice-faq-grid details p {
  border-color: rgb(148 163 184 / 0.2);
}

.dark .voice-product-mark span {
  background: rgb(27 167 206 / 0.18);
  color: #67e8f9;
}

.dark .voice-secondary {
  color: #e2e8f0;
}
.dark .voice-faq-section,
.dark .voice-related-section {
  background: #020617;
}

.dark .voice-related-section,
.dark .voice-related-list,
.dark .voice-related-list a {
  border-color: rgb(148 163 184 / 0.2);
}

.dark .voice-related-list a {
  color: #ffffff;
}
/* v3 (whiteboard, Oct 22), matching the story feature pages: the hero stacks a short headline
   over centred actions with the call card large under them, section headings drop to one calmer
   scale, and the definition reads as a quiet note. */
.voice-hero { background: linear-gradient(#ffffff 0%, #f6f9fc 100%); padding-bottom: 2.5rem; }
.dark .voice-hero { background: linear-gradient(#020617 0%, #07101f 100%); }
.voice-hero-grid { grid-template-columns: minmax(0, 1fr) !important; }
.voice-copy { max-width: 52rem; margin: 0 auto; text-align: center; }
.voice-copy h1 { max-width: none; font-size: clamp(2.4rem, 4.6vw, 3.9rem); font-weight: 760; letter-spacing: -0.032em; line-height: 1.04; }
.voice-copy p { margin-inline: auto; max-width: 40rem; }
.voice-actions { justify-content: center; }
.voice-copy :deep(.trial-disclosure) { margin-inline: auto; text-align: center; }
.voice-hero-story { width: 100%; max-width: 50rem; margin: 1rem auto 0; }
.voice-hero-story :deep(.sst) { background: transparent; }
.voice-hero-story :deep(.sst::before) { display: none; }
@media (min-width: 900px) { .voice-hero-story { zoom: 1.16; } }
@media (min-width: 1200px) { .voice-hero-story { zoom: 1.3; } }
.voice-page :deep(section h2) { font-size: clamp(1.9rem, 3.1vw, 2.65rem); font-weight: 740; letter-spacing: -0.026em; line-height: 1.08; }
.voice-definition-section { border-top: 1px solid #e2e8f0; border-bottom: 0; background: #ffffff; padding: 4.5rem 0; }
.dark .voice-definition-section { border-color: #1e293b; background: #020617; }
.voice-definition-grid { grid-template-columns: minmax(0, 1fr) !important; max-width: 52rem; gap: 1rem; }
.voice-page .voice-definition-grid h2 { max-width: none; font-size: clamp(1.45rem, 2.2vw, 1.8rem); font-weight: 700; letter-spacing: -0.015em; }
@media (max-width: 760px) {
  .voice-copy { text-align: left; }
  .voice-copy p { margin-inline: 0; }
  .voice-actions { justify-content: flex-start; }
  .voice-copy :deep(.trial-disclosure) { margin-inline: 0; text-align: left; }
  .voice-copy h1 { font-size: 2.1rem; }
  .voice-definition-section { padding: 2.75rem 0; }
  /* The call card comes right after the headline so it shows on the first screen; the promise,
     plan line and actions follow it. */
  .voice-hero-grid { display: flex; flex-direction: column; align-items: stretch; gap: 0; padding-top: 0.9rem; }
  /* Small phones stack the actions full width (the left alignment above must not shrink them). */
  .voice-actions { justify-content: stretch; }
  .voice-copy { display: contents; }
  .voice-hero-story { order: 1; margin: 0.6rem 0 0.4rem; }
  .voice-copy p,
  .voice-actions { order: 2; }
}
/* The terms sit inside the actions row: under the trial button when the buttons stack, under both when they share a row. */
.voice-actions :deep(.trial-disclosure) { flex: 0 0 100%; max-width: none; margin-block: 0; }
@media (min-width: 761px) { .voice-actions :deep(.trial-disclosure) { order: 2; } }
/* An app-screen hero is drawn at the app's own scale, so it gets width instead of a zoom: it breaks out of the
   68rem column to 78rem (90rem from 1600px), centred, and the copy above it runs wider and calmer. */
.voice-hero-story.voice-hero-story--app { --app-w: min(78rem, 100vw - 3rem); zoom: 1; width: var(--app-w); max-width: none; margin: 1rem calc(50% - var(--app-w) / 2) 0; }
@media (min-width: 1600px) { .voice-hero-story.voice-hero-story--app { --app-w: min(90rem, 100vw - 8rem); } }
@media (min-width: 761px) {
  .voice-hero:has(.voice-hero-story--app) { padding-top: 8.25rem; }
  .voice-hero:has(.voice-hero-story--app) .voice-hero-grid { gap: 1.5rem; }
  .voice-hero:has(.voice-hero-story--app) .voice-copy { max-width: 66rem; }
  .voice-hero:has(.voice-hero-story--app) .voice-copy h1 { font-size: clamp(2.3rem, 3.8vw, 3.25rem); }
  .voice-hero:has(.voice-hero-story--app) .voice-copy p { max-width: 52rem; }
}
@media (max-width: 760px) { .voice-hero-story.voice-hero-story--app { width: auto; margin: 0.6rem 0 2.2rem; } }
.voice-product-mark { color: #0e7490; font-size: 0.78rem; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; }
.voice-product-mark span { display: none; }
</style>
